import type { StorageReference } from "firebase/storage";
import { getDownloadURL, getStorage, ref } from "firebase/storage";
import { useEffect, useState } from "react";

// Storage security rules allow anyone to read media images, so their URLs need no download token.
const PUBLIC_PATH_PREFIX = "media/images/";

export function useStorageUrlWithCredentials(url: string | undefined) {
  const publicUrl = publicStorageUrl(url);
  const [signedUrl, setSignedUrl] = useState<string>();
  useEffect(() => {
    const resolveSignedUrl = async (privateUrl: string) => {
      setSignedUrl(await signedStorageUrl(privateUrl));
    };
    if (url && !publicUrl) {
      void resolveSignedUrl(url);
    } else {
      setSignedUrl(undefined);
    }
  }, [url, publicUrl]);
  return publicUrl ?? signedUrl;
}

export async function storageUrlWithCredentials(url: string | undefined): Promise<string | undefined> {
  if (url === undefined) {
    return undefined;
  }
  return publicStorageUrl(url) ?? (await signedStorageUrl(url));
}

function publicStorageUrl(url: string | undefined): string | undefined {
  const storageReference = url ? toStorageReference(url) : undefined;
  if (!storageReference?.fullPath.startsWith(PUBLIC_PATH_PREFIX)) {
    return undefined;
  }
  const encodedPath = encodeURIComponent(storageReference.fullPath);
  return `https://firebasestorage.googleapis.com/v0/b/${storageReference.bucket}/o/${encodedPath}?alt=media`;
}

async function signedStorageUrl(url: string): Promise<string | undefined> {
  try {
    return await getDownloadURL(ref(getStorage(), url));
  } catch (error) {
    console.warn(`Error getting download URL for [${url}]`, error);
    return undefined;
  }
}

function toStorageReference(url: string): StorageReference | undefined {
  try {
    return ref(getStorage(), url);
  } catch (error) {
    console.warn(`Invalid storage URL [${url}]`, error);
    return undefined;
  }
}
